/**
 * Admin API Service — generic CRUD calls to backend admin endpoints.
 * All calls go through /api/admin/... and require a JWT token.
 */

const getHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
    'Content-Type': 'application/json'
});

const getAuthHeader = () => ({
    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
});

async function apiCall<T>(url: string, options?: RequestInit): Promise<T> {
    const response = await fetch(`/api/admin${url}`, {
        ...options,
        headers: options?.headers || getHeaders()
    });
    if (!response.ok) {
        const err = await response.json().catch(() => ({ message: 'Erro desconhecido' }));
        throw new Error(err.message || `HTTP ${response.status}`);
    }
    return response.json();
}

// ─── Dashboard ───
export const adminApi = {
    getStats: () => apiCall<{ questions: number; categories: number; courses: number; diagnostics: number; users: number }>('/stats'),

    // ─── Homepage ───
    getHero: () => apiCall<any>('/homepage/hero'),
    updateHero: (data: any) => apiCall<any>('/homepage/hero', { method: 'PUT', body: JSON.stringify(data) }),

    getBenefits: () => apiCall<any>('/homepage/benefits'),
    updateBenefits: (data: any) => apiCall<any>('/homepage/benefits', { method: 'PUT', body: JSON.stringify(data) }),
    upsertBenefitCard: (data: any) => apiCall<any>('/homepage/benefits-cards', { method: 'POST', body: JSON.stringify(data) }),
    deleteBenefitCard: (id: number) => apiCall<any>(`/homepage/benefits-cards/${id}`, { method: 'DELETE' }),

    getFaq: () => apiCall<any>('/homepage/faq'),
    updateFaq: (data: any) => apiCall<any>('/homepage/faq', { method: 'PUT', body: JSON.stringify(data) }),
    upsertFaqQuestion: (data: any) => apiCall<any>('/homepage/faq-questions', { method: 'POST', body: JSON.stringify(data) }),
    deleteFaqQuestion: (id: number) => apiCall<any>(`/homepage/faq-questions/${id}`, { method: 'DELETE' }),

    getHowItWorks: () => apiCall<any>('/homepage/how-it-works'),
    updateHowItWorks: (data: any) => apiCall<any>('/homepage/how-it-works', { method: 'PUT', body: JSON.stringify(data) }),
    upsertHowItWorksCard: (data: any) => apiCall<any>('/homepage/how-it-works-cards', { method: 'POST', body: JSON.stringify(data) }),
    deleteHowItWorksCard: (id: number) => apiCall<any>(`/homepage/how-it-works-cards/${id}`, { method: 'DELETE' }),

    getMaturityExplanation: () => apiCall<any>('/homepage/maturity-explanation'),
    updateMaturityExplanation: (data: any) => apiCall<any>('/homepage/maturity-explanation', { method: 'PUT', body: JSON.stringify(data) }),
    upsertMaturityCard: (data: any) => apiCall<any>('/homepage/maturity-cards', { method: 'POST', body: JSON.stringify(data) }),
    deleteMaturityCard: (id: number) => apiCall<any>(`/homepage/maturity-cards/${id}`, { method: 'DELETE' }),

    getFooter: () => apiCall<any>('/homepage/footer'),
    updateFooter: (data: any) => apiCall<any>('/homepage/footer', { method: 'PUT', body: JSON.stringify(data) }),
    upsertFooterMenu: (data: any) => apiCall<any>('/homepage/footer-menu', { method: 'POST', body: JSON.stringify(data) }),
    deleteFooterMenu: (id: number) => apiCall<any>(`/homepage/footer-menu/${id}`, { method: 'DELETE' }),
    upsertFooterSocial: (data: any) => apiCall<any>('/homepage/footer-social', { method: 'POST', body: JSON.stringify(data) }),
    deleteFooterSocial: (id: number) => apiCall<any>(`/homepage/footer-social/${id}`, { method: 'DELETE' }),

    // ─── Quiz ───
    getQuizHeader: () => apiCall<any>('/quiz/header'),
    updateQuizHeader: (data: any) => apiCall<any>('/quiz/header', { method: 'PUT', body: JSON.stringify(data) }),
    getCategories: () => apiCall<any[]>('/quiz/categories'),
    updateCategory: (id: number, data: any) => apiCall<any>(`/quiz/categories/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    getQuizQuestions: () => apiCall<any[]>('/quiz/questions'),
    upsertQuizQuestion: (data: any) => apiCall<any>('/quiz/questions', { method: 'POST', body: JSON.stringify(data) }),
    deleteQuizQuestion: (id: number) => apiCall<any>(`/quiz/questions/${id}`, { method: 'DELETE' }),
    upsertQuizAnswer: (data: any) => apiCall<any>('/quiz/answers', { method: 'POST', body: JSON.stringify(data) }),
    deleteQuizAnswer: (id: number) => apiCall<any>(`/quiz/answers/${id}`, { method: 'DELETE' }),

    // ─── Results & Levels ───
    getLevels: () => apiCall<any[]>('/results/levels'),
    updateLevel: (id: number, data: any) => apiCall<any>(`/results/levels/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    getResultsText: () => apiCall<any>('/results/text'),
    updateResultsText: (data: any) => apiCall<any>('/results/text', { method: 'PUT', body: JSON.stringify(data) }),
    getLevelInsights: () => apiCall<any[]>('/results/level-insights'),
    upsertLevelInsight: (data: any) => apiCall<any>('/results/level-insights', { method: 'POST', body: JSON.stringify(data) }),
    getLevelRanges: () => apiCall<any[]>('/results/level-ranges'),
    updateLevelRange: (id: number, data: any) => apiCall<any>(`/results/level-ranges/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    getCategoryInsights: () => apiCall<any[]>('/results/category-insights'),
    upsertCategoryInsight: (data: any) => apiCall<any>('/results/category-insights', { method: 'POST', body: JSON.stringify(data) }),

    // ─── Courses ───
    getCourses: () => apiCall<any[]>('/courses'),
    upsertCourse: (data: any) => apiCall<any>('/courses', { method: 'POST', body: JSON.stringify(data) }),
    deleteCourse: (id: number) => apiCall<any>(`/courses/${id}`, { method: 'DELETE' }),

    // ─── Upload ───
    uploadFile: async (file: File): Promise<{ success: boolean; url: string; filename: string }> => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await fetch('/api/admin/upload', {
            method: 'POST',
            headers: getAuthHeader(),
            body: formData
        });
        if (!response.ok) throw new Error('Falha no upload');
        return response.json();
    }
};
